import { Link } from 'react-router-dom';
import { getAllPosts } from '../content/blog/posts';

function formatDate(date) {
  return new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function Blog() {
  const posts = getAllPosts();
  const [featured, ...rest] = posts;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page header — kicker → headline → standfirst */}
      <header className="mb-10 sm:mb-14">
        <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-on-surface-variant mb-3">
          The Blog
        </span>
        <h1 className="font-headline font-expanded text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold tracking-tight text-on-surface mb-4">
          Stories from below the radar
        </h1>
        <p className="max-w-2xl text-base sm:text-lg text-on-surface-variant leading-relaxed">
          Long-form writing on non-league football's most interesting stories, players, and data.
        </p>
      </header>

      {posts.length > 0 && (
        <>
          {/* Featured story — most recent post */}
          <Link
            to={`/blog/${featured.slug}`}
            className="group block overflow-hidden rounded-xl border border-outline-variant/30 bg-surface-container transition-all duration-200 hover:border-primary/40 hover:bg-surface-container-high active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 mb-10 sm:mb-14"
          >
            <div className={featured.image ? 'grid lg:grid-cols-2' : ''}>
              {featured.image && (
                <div className="relative aspect-[1200/630] lg:aspect-auto overflow-hidden">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-secondary px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-on-secondary">
                    Latest
                  </span>
                </div>
              )}
              <div className="flex flex-col justify-center p-6 sm:p-8 lg:p-10">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-on-surface-variant mb-4">
                  <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-primary">
                    {featured.kicker || 'Player Watch'}
                  </span>
                  <span aria-hidden="true">&middot;</span>
                  <time dateTime={featured.date}>{formatDate(featured.date)}</time>
                  <span aria-hidden="true">&middot;</span>
                  <span>{featured.readingTime} min read</span>
                </div>
                <h2 className="font-headline font-expanded text-2xl sm:text-3xl font-bold leading-tight tracking-tight text-on-surface transition-colors duration-150 group-hover:text-primary mb-3">
                  {featured.title}
                </h2>
                <p className="text-sm sm:text-base text-on-surface-variant leading-relaxed line-clamp-3 mb-6">
                  {featured.description}
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                  Read the story
                  <span className="material-symbols-outlined text-[18px] transition-transform duration-150 group-hover:translate-x-0.5">
                    arrow_forward
                  </span>
                </span>
              </div>
            </div>
          </Link>

          {rest.length > 0 && (
            <section>
              <div className="flex items-center gap-4 mb-6 sm:mb-8">
                <span className="text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">
                  More stories
                </span>
                <span className="h-px flex-1 bg-outline-variant/40" aria-hidden="true" />
              </div>
              <div className="grid gap-5 sm:gap-6 sm:grid-cols-2">
                {rest.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group flex flex-col overflow-hidden rounded-xl border border-outline-variant/30 bg-surface-container transition-all duration-200 hover:border-primary/40 hover:bg-surface-container-high active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
                  >
                    {post.image && (
                      <div className="aspect-[1200/630] overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                        />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col p-5 sm:p-6">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-on-surface-variant mb-3">
                        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-primary">
                          {post.kicker || 'Player Watch'}
                        </span>
                        <span aria-hidden="true">&middot;</span>
                        <time dateTime={post.date}>{formatDate(post.date)}</time>
                      </div>
                      <h2 className="font-headline text-lg sm:text-xl font-bold leading-snug text-on-surface transition-colors duration-150 group-hover:text-primary mb-2">
                        {post.title}
                      </h2>
                      <p className="text-sm text-on-surface-variant leading-relaxed line-clamp-3 mb-5">
                        {post.description}
                      </p>
                      <div className="mt-auto flex flex-wrap items-center gap-2">
                        {post.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full border border-outline-variant/40 bg-surface-container-highest px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant"
                          >
                            {tag}
                          </span>
                        ))}
                        <span className="ml-auto text-xs text-on-surface-variant/70">
                          {post.readingTime} min
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {posts.length === 0 && (
        <div className="rounded-xl border border-dashed border-outline-variant/50 py-20 text-center">
          <span className="material-symbols-outlined mb-4 block text-5xl text-on-surface-variant">article</span>
          <p className="text-on-surface-variant">No posts yet. Check back soon.</p>
        </div>
      )}
    </div>
  );
}
